import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { buildQuickAppointmentPayload } from "@/lib/quickAppointment";
import {
  businessLocalDateTimeToIso,
  isHistoricalAppointmentEnd,
} from "../lib/historicalAppointment";
import { toast } from "sonner";
import { formatCurrencyEUR, formatDateTimeIT } from "@/lib/formatters";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppointmentWizardMobile } from "@/components/AppointmentWizardMobile";
import { OwnerAppointmentSuccess } from "../components/OwnerAppointmentSuccess";
import { PageHeader } from "../components/PageHeader";
import { DateField, DateTimeField, formatDateInputValueIT } from "@/components/TemporalField";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AGENDA_SECTION_DEFINITIONS,
  buildAgendaDaySummary,
  buildAgendaMonthDays,
  buildAgendaRows,
  buildAgendaSections,
  buildAgendaWeekDays,
  classifyAppointment,
  DEFAULT_BUSINESS_TIMEZONE,
  formatAgendaDayLabel,
  formatAgendaMonthLabel,
  formatAgendaWeekContextLabel,
  formatAgendaWeekLabel,
  getAgendaMonthGridRange,
  getNextClientState,
  shiftDateKey,
  shiftMonthKey,
  shiftWeekKey,
  toBusinessDateKey,
} from "@/lib/agendaView";

const toIso = (value) => (value ? new Date(value).toISOString() : null);
const formatAgendaAppointmentDateTime = (value, timeZone) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const day = new Intl.DateTimeFormat("it-IT", {
    timeZone,
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  const time = new Intl.DateTimeFormat("it-IT", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  return `${day}, ${time}`;
};
const formatAgendaAppointmentTime = (value, timeZone) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("it-IT", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};
const MOBILE_MEDIA_QUERY = "(max-width: 767px)";
const AGENDA_REFRESH_INTERVAL_MS = 120000;
const AUTOMATIC_REFRESH_DEDUP_MS = 5000;

const mergeAgendaAppointments = (...collections) => {
  const appointmentsById = new Map();
  collections.flat().forEach((appointment) => {
    if (appointment?.id) appointmentsById.set(appointment.id, appointment);
  });
  return Array.from(appointmentsById.values());
};

const getAgendaWindowParams = (selectedDateKey, timeZone) => {
  const { startDateKey, endDateKey } = getAgendaMonthGridRange(selectedDateKey);
  const start = businessLocalDateTimeToIso(`${startDateKey}T00:00`, timeZone);
  const endExclusive = businessLocalDateTimeToIso(
    `${shiftDateKey(endDateKey, 1)}T00:00`,
    timeZone
  );
  const end = endExclusive
    ? new Date(Date.parse(endExclusive) - 1).toISOString()
    : null;
  if (!start || !end) throw new Error("Invalid Agenda window");
  return {
    scopeKey: `${timeZone}:${startDateKey}:${endDateKey}`,
    params: { start, end },
  };
};

const fetchAgendaAppointments = async (
  selectedDateKey,
  timeZone,
  now = new Date()
) => {
  const { scopeKey, params: windowParams } = getAgendaWindowParams(
    selectedDateKey,
    timeZone
  );
  const nowIso = now.toISOString();
  const [windowResponse, closingResponse, nextResponse] = await Promise.all([
    api.get("/appointments", { params: windowParams }),
    api.get("/appointments", {
      params: { end: nowIso, status_filter: "scheduled" },
    }),
    api.get("/appointments", {
      params: { start: nowIso, status_filter: "scheduled", limit: 1 },
    }),
  ]);
  return {
    appointments: mergeAgendaAppointments(
      Array.isArray(windowResponse.data) ? windowResponse.data : [],
      Array.isArray(closingResponse.data) ? closingResponse.data : [],
      Array.isArray(nextResponse.data) ? nextResponse.data : []
    ),
    scopeKey,
  };
};
const CANCELLATION_ACTOR_LABELS = {
  owner: "titolare",
  staff: "staff",
  customer: "cliente",
  system: "sistema",
};
const toDateTimeLocalValue = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};
const getServiceDurationMinutes = (service) => {
  if (!service) return null;
  const candidates = [
    service.duration_minutes,
    service.durationMinutes,
    service.duration,
    service.minutes,
  ];
  for (const raw of candidates) {
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
};
const getBusinessHoursErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  const detailMessage = typeof detail === "string" ? detail : detail?.message;
  if (typeof detailMessage !== "string") return "";
  if (detailMessage.includes("closed on the selected day")) {
    return "Il negozio è chiuso in questo orario.";
  }
  if (detailMessage.includes("working hours") || detailMessage.includes("single local day")) {
    return "Questo appuntamento supera l’orario di apertura del negozio.";
  }
  return "";
};
export const getAppointmentDurationMinutes = (appointment) => {
  const startTimestamp = Date.parse(appointment?.start_time);
  const endTimestamp = Date.parse(appointment?.end_time);
  if (!Number.isFinite(startTimestamp) || !Number.isFinite(endTimestamp) || endTimestamp <= startTimestamp) {
    return null;
  }
  const durationMinutes = (endTimestamp - startTimestamp) / 60000;
  return Number.isInteger(durationMinutes) ? durationMinutes : null;
};
export const getRescheduleExpectedEnd = (startTime, durationMinutes) => {
  const startTimestamp = Date.parse(startTime);
  if (!Number.isFinite(startTimestamp) || !Number.isFinite(durationMinutes) || durationMinutes <= 0) {
    return "";
  }
  return new Date(startTimestamp + durationMinutes * 60000).toISOString();
};
const getRescheduleErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  const detailMessage = typeof detail === "string" ? detail : detail?.message;
  if (error?.response?.status === 409 || detail?.conflict_type || detailMessage?.includes("overlaps")) {
    return "Questo orario non è disponibile. Scegline un altro.";
  }
  const businessHoursMessage = getBusinessHoursErrorMessage(error);
  if (businessHoursMessage) return businessHoursMessage;
  return "Impossibile spostare l’appuntamento. Scegli un altro orario e riprova.";
};
const showTestingTools =
  process.env.NODE_ENV !== "production" &&
  process.env.REACT_APP_ENABLE_TESTING_TOOLS === "true";

const DEFAULT_APPOINTMENT_SUBMIT_ERROR = "Non riesco a creare l'appuntamento.";
const SLOT_UNAVAILABLE_ERROR = "Lo slot scelto non è disponibile.";
const RISKY_ACTION_COPY = {
  cancel: {
    title: "Annullare l’appuntamento?",
    consequence: "L’appuntamento verrà annullato e la fascia tornerà disponibile per nuove prenotazioni.",
    confirmLabel: "Conferma annullamento",
    errorMessage: "Impossibile annullare l’appuntamento. Puoi riprovare o tornare indietro.",
  },
  complete: {
    title: "Segnare l’appuntamento come svolto?",
    consequence: "L’appuntamento verrà spostato tra i completati.",
    confirmLabel: "Conferma Svolto",
    errorMessage: "Impossibile completare l’appuntamento. Puoi riprovare o tornare indietro.",
  },
  noshow: {
    title: "Registrare una mancata presentazione?",
    consequence: "L’appuntamento verrà registrato come mancata presentazione.",
    confirmLabel: "Conferma no-show",
    errorMessage: "Impossibile segnare il no-show. Puoi riprovare o tornare indietro.",
  },
  deleteBlockedTime: {
    title: "Eliminare il blocco orario?",
    consequence: "La fascia potrà tornare prenotabile.",
    confirmLabel: "Conferma eliminazione",
    errorMessage: "Impossibile eliminare il blocco orario. Puoi riprovare o tornare indietro.",
  },
};

const AgendaDayCountBadge = ({ count, selected, testId }) => {
  if (count <= 0) return null;
  return (
    <span
      className={`mx-auto mt-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] ${
        selected ? "bg-white/20" : "bg-emerald-100 text-emerald-800"
      }`}
      data-testid={testId}
    >
      {count}
    </span>
  );
};

const extractAppointmentSubmitError = (error) => {
  const businessHoursMessage = getBusinessHoursErrorMessage(error);
  if (businessHoursMessage) return businessHoursMessage;
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    const normalizedDetail = detail.trim();
    if (normalizedDetail === "Requested slot overlaps with an existing appointment") {
      return SLOT_UNAVAILABLE_ERROR;
    }
    if (normalizedDetail === "Requested slot overlaps with a blocked time") {
      return SLOT_UNAVAILABLE_ERROR;
    }
    return normalizedDetail;
  }
  if (typeof detail?.message === "string" && detail.message.trim()) {
    const normalizedMessage = detail.message.trim();
    if (normalizedMessage === "Requested slot overlaps with an existing appointment") {
      return SLOT_UNAVAILABLE_ERROR;
    }
    if (normalizedMessage === "Requested slot overlaps with a blocked time") {
      return SLOT_UNAVAILABLE_ERROR;
    }
    return normalizedMessage;
  }
  return DEFAULT_APPOINTMENT_SUBMIT_ERROR;
};

const extractAppointmentSubmitAlternatives = (error) => {
  const detail = error?.response?.data?.detail;
  const sameDay = Array.isArray(detail?.same_day_alternatives)
    ? detail.same_day_alternatives.filter(
      (slot) => slot && typeof slot.start_time === "string" && typeof slot.end_time === "string"
    )
    : [];
  const nearby = Array.isArray(detail?.nearby_alternatives)
    ? detail.nearby_alternatives.filter(
      (slot) => slot && typeof slot.start_time === "string" && typeof slot.end_time === "string"
    )
    : [];

  return {
    same_day: sameDay,
    nearby,
  };
};

export function AgendaPage({ mode = "agenda" }) {
  const { isOwner } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkedDate = /^\d{4}-\d{2}-\d{2}$/.test(searchParams.get("date") || "")
    ? searchParams.get("date")
    : "";
  const linkedAppointmentId = searchParams.get("appointment") || "";
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agendaLoadState, setAgendaLoadState] = useState("loading");
  const [agendaRefreshError, setAgendaRefreshError] = useState("");
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [customerMode, setCustomerMode] = useState("existing");
  const [appointmentForm, setAppointmentForm] = useState({
    customer_id: "",
    service_id: "",
    staff_id: "",
    start_time: "",
    end_time: "",
    historical_outcome: "",
  });
  const [quickCustomerForm, setQuickCustomerForm] = useState({
    name: "",
    phone: "",
    email: "",
  });
  const [seedLoading, setSeedLoading] = useState(false);
  const [lastScenarioId, setLastScenarioId] = useState("");
  const [availabilityForm, setAvailabilityForm] = useState({
    customer_id: "",
    service_id: "",
    staff_id: "unassigned",
    date: "",
    requested_start_time: "",
  });
  const [availabilityResult, setAvailabilityResult] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [appointmentEndTimeManual, setAppointmentEndTimeManual] = useState(false);
  const [recentlyCreatedAppointmentId, setRecentlyCreatedAppointmentId] = useState("");
  const [appointmentWizardResetToken, setAppointmentWizardResetToken] = useState(0);
  const [appointmentSuccessFeedback, setAppointmentSuccessFeedback] = useState(null);
  const [blockedTimeForm, setBlockedTimeForm] = useState({
    staff_id: "",
    start_time: "",
    end_time: "",
    reason: "",
  });
  const [blockedTimesLoading, setBlockedTimesLoading] = useState(false);
  const [blockedTimeSubmitting, setBlockedTimeSubmitting] = useState(false);
  const [blockedTimeDeletingId, setBlockedTimeDeletingId] = useState("");
  const [reschedulingAppointmentId, setReschedulingAppointmentId] = useState("");
  const [rescheduleForm, setRescheduleForm] = useState({
    start_time: "",
  });
  const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false);
  const [closingAppointmentId, setClosingAppointmentId] = useState("");
  const [selectedAgendaSection, setSelectedAgendaSection] = useState("day");
  const [calendarView, setCalendarView] = useState("month");
  const [selectedDayKey, setSelectedDayKey] = useState(() => toBusinessDateKey(new Date()));
  const [jumpDate, setJumpDate] = useState(() => toBusinessDateKey(new Date()));
  const [agendaNow, setAgendaNow] = useState(() => new Date());
  const [businessTimezone, setBusinessTimezone] = useState(DEFAULT_BUSINESS_TIMEZONE);
  const [pendingRiskyAction, setPendingRiskyAction] = useState(null);
  const [riskyActionDialogOpen, setRiskyActionDialogOpen] = useState(false);
  const [riskyActionSubmitting, setRiskyActionSubmitting] = useState(false);
  const [riskyActionError, setRiskyActionError] = useState("");
  const [completedRiskyAction, setCompletedRiskyAction] = useState(null);
  const [highlightedAppointmentId, setHighlightedAppointmentId] = useState("");
  const riskyActionSubmittingRef = useRef(false);
  const successfulRiskyActionRef = useRef(null);
  const riskyActionBackRef = useRef(null);
  const agendaSectionRef = useRef(null);
  const blockedTimesSectionRef = useRef(null);
  const blockingErrorToastIdRef = useRef(null);
  const loadedAppointmentScopeRef = useRef("");
  const appointmentsRequestIdRef = useRef(0);
  const refreshAppointmentsRef = useRef(null);
  const lastAutomaticRefreshAtRef = useRef(0);
  const selectedDayKeyRef = useRef(selectedDayKey);
  const businessTimezoneRef = useRef(businessTimezone);
  selectedDayKeyRef.current = selectedDayKey;
  businessTimezoneRef.current = businessTimezone;

  const dismissBlockingError = () => {
    if (blockingErrorToastIdRef.current !== null) {
      toast.dismiss(blockingErrorToastIdRef.current);
      blockingErrorToastIdRef.current = null;
    }
  };

  const showBlockingError = (message) => {
    dismissBlockingError();
    blockingErrorToastIdRef.current = toast.error(message, {
      duration: Infinity,
      closeButton: true,
    });
  };

  useEffect(() => {
    if (linkedDate) {
      setSelectedDayKey(linkedDate);
      setJumpDate(linkedDate);
      setSelectedAgendaSection("day");
      setHighlightedAppointmentId(linkedAppointmentId);
    }
  }, [linkedAppointmentId, linkedDate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setAgendaLoadState("loading");
    setAgendaRefreshError("");
    try {
      const businessHoursRes = await api
        .get("/business-hours")
        .catch(() => ({ data: { timezone: DEFAULT_BUSINESS_TIMEZONE } }));
      const resolvedTimezone =
        businessHoursRes.data?.timezone || DEFAULT_BUSINESS_TIMEZONE;
      const [
        agendaAppointments,
        servicesRes,
        customersRes,
        staffRes,
        blockedTimesRes,
      ] =
        await Promise.all([
          fetchAgendaAppointments(selectedDayKeyRef.current, resolvedTimezone),
          api.get("/services"),
          api.get("/customers"),
          api.get("/staff"),
          api.get("/blocked-times"),
        ]);
      setAppointments(agendaAppointments.appointments);
      loadedAppointmentScopeRef.current = agendaAppointments.scopeKey;
      setServices(servicesRes.data);
      setCustomers(customersRes.data);
      setStaff(staffRes.data);
      setBlockedTimes(blockedTimesRes.data);
      setBusinessTimezone(resolvedTimezone);
      setAgendaLoadState("success");
    } catch (error) {
      toast.error("Caricamento agenda non riuscito.");
      setAgendaLoadState("failure");
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAppointments = useCallback(async () => {
    const requestId = appointmentsRequestIdRef.current + 1;
    appointmentsRequestIdRef.current = requestId;
    try {
      const agendaAppointments = await fetchAgendaAppointments(
        selectedDayKeyRef.current,
        businessTimezoneRef.current
      );
      if (appointmentsRequestIdRef.current !== requestId) return;
      setAppointments(agendaAppointments.appointments);
      loadedAppointmentScopeRef.current = agendaAppointments.scopeKey;
      setAgendaRefreshError("");
    } catch (error) {
      if (appointmentsRequestIdRef.current !== requestId) return;
      setAgendaRefreshError(
        "Aggiornamento automatico non riuscito. I dati mostrati potrebbero non essere aggiornati."
      );
    }
  }, []);
  refreshAppointmentsRef.current = refreshAppointments;

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (agendaLoadState !== "success") return;
    const { scopeKey } = getAgendaWindowParams(
      selectedDayKey,
      businessTimezone
    );
    if (loadedAppointmentScopeRef.current !== scopeKey) {
      refreshAppointments();
    }
  }, [agendaLoadState, businessTimezone, refreshAppointments, selectedDayKey]);

  useEffect(() => {
    if (mode !== "agenda") return undefined;
    const triggerAutomaticRefresh = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState &&
        document.visibilityState !== "visible"
      ) {
        return;
      }
      const now = Date.now();
      if (now - lastAutomaticRefreshAtRef.current < AUTOMATIC_REFRESH_DEDUP_MS) {
        return;
      }
      lastAutomaticRefreshAtRef.current = now;
      refreshAppointmentsRef.current?.();
    };
    const handleVisibilityChange = () => triggerAutomaticRefresh();
    window.addEventListener("focus", triggerAutomaticRefresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const refreshTimerId = window.setInterval(
      triggerAutomaticRefresh,
      AGENDA_REFRESH_INTERVAL_MS
    );
    return () => {
      window.removeEventListener("focus", triggerAutomaticRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(refreshTimerId);
    };
  }, [mode]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const syncViewport = () => {
      setIsMobileViewport(mediaQuery.matches);
    };
    syncViewport();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncViewport);
      return () => mediaQuery.removeEventListener("change", syncViewport);
    }
    mediaQuery.addListener(syncViewport);
    return () => mediaQuery.removeListener(syncViewport);
  }, []);

  useEffect(() => {
    const timerId = window.setInterval(() => setAgendaNow(new Date()), 60000);
    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    const completedAction = completedRiskyAction;
    if (!completedAction || loading || riskyActionDialogOpen) {
      return undefined;
    }

    const focusTimerId = window.setTimeout(() => {
      const targetRef = completedAction === "deleteBlockedTime" ? blockedTimesSectionRef : agendaSectionRef;
      targetRef.current?.focus({ preventScroll: true });
      successfulRiskyActionRef.current = null;
      setCompletedRiskyAction(null);
    }, 0);
    return () => window.clearTimeout(focusTimerId);
  }, [appointments, blockedTimes, completedRiskyAction, loading, riskyActionDialogOpen]);

  const serviceMap = useMemo(() => {
    return services.reduce((acc, service) => {
      acc[service.id] = service;
      return acc;
    }, {});
  }, [services]);

  const activeServices = useMemo(() => {
    return services.filter((service) => service.active !== false);
  }, [services]);

  const customerMap = useMemo(() => {
    return customers.reduce((acc, customer) => {
      acc[customer.id] = customer;
      return acc;
    }, {});
  }, [customers]);

  const staffMap = useMemo(() => {
    return staff.reduce((acc, member) => {
      acc[member.id] = member;
      return acc;
    }, {});
  }, [staff]);

  const activeStaff = useMemo(() => {
    return staff.filter((member) => member.active !== false);
  }, [staff]);

  const sortedAppointments = useMemo(() => {
    return [...appointments].sort((left, right) => {
      const leftMs = left?.start_time ? Date.parse(left.start_time) : Number.NaN;
      const rightMs = right?.start_time ? Date.parse(right.start_time) : Number.NaN;
      const leftValid = Number.isFinite(leftMs);
      const rightValid = Number.isFinite(rightMs);
      if (!leftValid && !rightValid) return 0;
      if (!leftValid) return 1;
      if (!rightValid) return -1;
      return leftMs - rightMs;
    });
  }, [appointments]);

  const agendaSections = useMemo(
    () => buildAgendaSections(sortedAppointments, agendaNow),
    [agendaNow, sortedAppointments]
  );
  const nextClientState = useMemo(
    () => getNextClientState(sortedAppointments, agendaNow, businessTimezone),
    [agendaNow, businessTimezone, sortedAppointments]
  );
  const selectedDayAppointments = useMemo(
    () => sortedAppointments.filter(
      (appointment) => toBusinessDateKey(appointment.start_time, businessTimezone) === selectedDayKey
    ),
    [businessTimezone, selectedDayKey, sortedAppointments]
  );
  const selectedDaySummary = useMemo(
    () => buildAgendaDaySummary(selectedDayAppointments, agendaNow),
    [agendaNow, selectedDayAppointments]
  );
  const isGlobalClosingView = selectedAgendaSection === "closing";
  const displayedAppointments = useMemo(() => {
    if (isGlobalClosingView) return agendaSections.closing;
    if (selectedAgendaSection === "day") return selectedDayAppointments;
    return selectedDaySummary.sections[selectedAgendaSection] || [];
  }, [agendaSections, isGlobalClosingView, selectedAgendaSection, selectedDayAppointments, selectedDaySummary]);
  const agendaMonthDays = useMemo(
    () => buildAgendaMonthDays(selectedDayKey, sortedAppointments, businessTimezone, agendaNow),
    [agendaNow, businessTimezone, selectedDayKey, sortedAppointments]
  );
  const agendaWeekDays = useMemo(
    () => buildAgendaWeekDays(selectedDayKey, sortedAppointments, businessTimezone, agendaNow),
    [agendaNow, businessTimezone, selectedDayKey, sortedAppointments]
  );
  const agendaRows = buildAgendaRows(displayedAppointments, businessTimezone);
  const todayKey = toBusinessDateKey(agendaNow, businessTimezone);

  const showSelectedDay = (dateKey) => {
    setSelectedDayKey(dateKey);
    setJumpDate(dateKey);
    setSelectedAgendaSection("day");
    setHighlightedAppointmentId("");
  };

  useEffect(() => {
    if (!highlightedAppointmentId || isGlobalClosingView) return undefined;
    const focusTimerId = window.setTimeout(() => {
      const appointmentCard = document.getElementById(`agenda-appointment-focus-${highlightedAppointmentId}`);
      if (!appointmentCard) return;
      appointmentCard.scrollIntoView({ behavior: "smooth", block: "center" });
      appointmentCard.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(focusTimerId);
  }, [displayedAppointments, highlightedAppointmentId, isGlobalClosingView]);

  const handleAppointmentFieldChange = (field, value, options = {}) => {
    const isManualEndTime = options.manualEndTime === true;
    const forceAutoFillEndTime = options.forceAutoFillEndTime === true;
    setAppointmentForm((prev) => {
      const next = { ...prev, [field]: value };
      const serviceId = field === "service_id" ? value : next.service_id;
      const startTime = field === "start_time" ? value : next.start_time;
      const service = services.find((item) => item.id === serviceId);
      const durationMinutes = getServiceDurationMinutes(service);
      const shouldAutoFillEnd =
        Boolean(startTime) &&
        Boolean(durationMinutes) &&
        (
          next.end_time === "" ||
          !appointmentEndTimeManual ||
          field === "service_id" ||
          forceAutoFillEndTime
        );
      if (shouldAutoFillEnd) {
        const startDate = new Date(startTime);
        if (!Number.isNaN(startDate.getTime())) {
          const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
          next.end_time = toDateTimeLocalValue(endDate);
        }
      }
      return next;
    });
    if (isManualEndTime) {
      setAppointmentEndTimeManual(true);
    } else if (field === "service_id" || forceAutoFillEndTime) {
      setAppointmentEndTimeManual(false);
    }
  };

  const handleAppointmentChange = (event) => {
    handleAppointmentFieldChange(event.target.name, event.target.value, {
      manualEndTime: event.target.name === "end_time",
    });
  };

  const handleQuickCustomerChange = (field, value) => {
    setQuickCustomerForm((prev) => ({ ...prev, [field]: value }));
    setAppointmentForm((prev) => {
      if (!prev.customer_id) {
        return prev;
      }
      return { ...prev, customer_id: "" };
    });
  };

  const handleCustomerModeChange = (mode) => {
    setCustomerMode(mode);
    setAppointmentForm((prev) => ({
      ...prev,
      customer_id: mode === "quick" ? "" : prev.customer_id,
    }));
  };

  const resetAppointmentState = () => {
    setAppointmentForm({
      customer_id: "",
      service_id: "",
      staff_id: "",
      start_time: "",
      end_time: "",
      historical_outcome: "",
    });
    setQuickCustomerForm({ name: "", phone: "", email: "" });
    setCustomerMode("existing");
    setAppointmentEndTimeManual(false);
    setAppointmentWizardResetToken((prev) => prev + 1);
  };

  const handleCreateAnotherAppointment = () => {
    dismissBlockingError();
    setRecentlyCreatedAppointmentId("");
    resetAppointmentState();
    setAppointmentSuccessFeedback(null);
  };

  const handleBackToAgenda = () => {
    setAppointmentSuccessFeedback(null);
    navigate("/");
  };

  const loadBlockedTimes = async () => {
    setBlockedTimesLoading(true);
    try {
      const response = await api.get("/blocked-times");
      setBlockedTimes(response.data);
      return response.data;
    } catch (error) {
      toast.error("Caricamento blocchi orari non riuscito.");
      return null;
    } finally {
      setBlockedTimesLoading(false);
    }
  };

  const refreshAvailabilityResult = async () => {
    if (!availabilityResult || !availabilityForm.service_id || !availabilityForm.date) {
      return;
    }
    try {
      const params = {
        service_id: availabilityForm.service_id,
        date: availabilityForm.date,
      };
      if (availabilityForm.staff_id && availabilityForm.staff_id !== "unassigned") {
        params.staff_id = availabilityForm.staff_id;
      }
      if (availabilityForm.requested_start_time) {
        params.requested_start_time = toIso(availabilityForm.requested_start_time);
      }
      const response = await api.get("/availability", { params });
      setAvailabilityResult(response.data);
      if (
        selectedSlot &&
        !response.data.available_slots?.some((slot) => slot.start_time === selectedSlot.start_time)
      ) {
        setSelectedSlot(null);
      }
    } catch (error) {
      setAvailabilityResult(null);
      setSelectedSlot(null);
      toast.error("Aggiornamento disponibilità non riuscito.");
    }
  };

  const handleBlockedTimeFieldChange = (field, value) => {
    setBlockedTimeForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetBlockedTimeForm = () => {
    setBlockedTimeForm({
      staff_id: "",
      start_time: "",
      end_time: "",
      reason: "",
    });
  };

  const submitBlockedTime = async (event) => {
    event.preventDefault();
    if (!blockedTimeForm.staff_id || !blockedTimeForm.start_time || !blockedTimeForm.end_time) {
      toast.error("Compila staff, inizio e fine blocco.");
      return;
    }
    setBlockedTimeSubmitting(true);
    try {
      await api.post("/blocked-times", {
        staff_id: blockedTimeForm.staff_id,
        start_time: toIso(blockedTimeForm.start_time),
        end_time: toIso(blockedTimeForm.end_time),
        reason: blockedTimeForm.reason.trim() || null,
      });
      toast.success("Blocco orario creato.");
      resetBlockedTimeForm();
      await Promise.all([loadBlockedTimes(), refreshAvailabilityResult()]);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Impossibile creare il blocco orario.");
      }
    } finally {
      setBlockedTimeSubmitting(false);
    }
  };

  const deleteBlockedTime = async (blockedTimeId) => {
    setBlockedTimeDeletingId(blockedTimeId);
    try {
      await api.delete(`/blocked-times/${blockedTimeId}`);
      toast.success("Blocco orario eliminato.");
      await Promise.all([loadBlockedTimes(), refreshAvailabilityResult()]);
      return true;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Impossibile eliminare il blocco orario.");
      }
      return false;
    } finally {
      setBlockedTimeDeletingId("");
    }
  };

  const submitAppointment = async (event) => {
    dismissBlockingError();
    const submitOptions =
      event && typeof event === "object" && !("preventDefault" in event) ? event : {};
    if (event?.preventDefault) {
      event.preventDefault();
    }
    if (!appointmentForm.service_id) {
      const errorMessage = "Seleziona un servizio.";
      toast.error(errorMessage);
      return { ok: false, errorMessage, alternatives: { same_day: [], nearby: [] } };
    }
    if (!appointmentForm.start_time) {
      const errorMessage = "Seleziona l'orario di inizio.";
      toast.error(errorMessage);
      return { ok: false, errorMessage, alternatives: { same_day: [], nearby: [] } };
    }
    try {
      const customerId = appointmentForm.customer_id;
      let quickName = "";
      let createdCustomerData = null;
      let appointmentData = null;
      if (customerMode === "quick") {
        quickName = quickCustomerForm.name.trim();
        if (!quickName) {
          const errorMessage = "Inserisci il nome del cliente rapido.";
          toast.error(errorMessage);
          return { ok: false, errorMessage, alternatives: { same_day: [], nearby: [] } };
        }
      } else if (!customerId) {
        const errorMessage = "Seleziona un cliente.";
        toast.error(errorMessage);
        return { ok: false, errorMessage, alternatives: { same_day: [], nearby: [] } };
      }

      const isHistoricalAppointment = isOwner && isHistoricalAppointmentEnd({
        endTime: appointmentForm.end_time,
        timeZone: businessTimezone,
      });
      const historicalOutcome = isHistoricalAppointment ? appointmentForm.historical_outcome : "";
      if (isHistoricalAppointment && !historicalOutcome) {
        const errorMessage = "Scegli come registrare l’appuntamento passato.";
        toast.error(errorMessage);
        return { ok: false, errorMessage, alternatives: { same_day: [], nearby: [] } };
      }
      const toAppointmentIso = (value) => businessLocalDateTimeToIso(value, businessTimezone);

      if (customerMode === "quick") {
        const response = await api.post(
          "/appointments/quick-customer",
          buildQuickAppointmentPayload({
            appointmentForm: { ...appointmentForm, historical_outcome: historicalOutcome },
            quickCustomerForm,
            toIso: toAppointmentIso,
          })
        );
        appointmentData = response.data.appointment;
        createdCustomerData = response.data.customer;
      } else {
        const response = await api.post("/appointments", {
          customer_id: customerId,
          service_id: appointmentForm.service_id,
          staff_id: appointmentForm.staff_id === "unassigned" ? null : appointmentForm.staff_id,
          status: "scheduled",
          start_time: toAppointmentIso(appointmentForm.start_time),
          end_time: toAppointmentIso(appointmentForm.end_time),
          ...(historicalOutcome ? { historical_outcome: historicalOutcome } : {}),
        });
        appointmentData = response.data;
      }
      const savedCustomer =
        customers.find((item) => item.id === appointmentData?.customer_id) ||
        (createdCustomerData?.id === appointmentData?.customer_id ? createdCustomerData : null);
      const savedService = services.find((item) => item.id === appointmentData?.service_id);
      const savedStaff = staff.find((item) => item.id === appointmentData?.staff_id);
      const successSummary = {
        appointment: { ...appointmentData },
        customerName: savedCustomer?.name || "Cliente non disponibile",
        serviceName: savedService?.name || "Servizio non disponibile",
        staffName: appointmentData?.staff_id
          ? savedStaff?.name || "Operatrice non disponibile"
          : "Non assegnata",
      };
      toast.success("Appuntamento creato.");
      setRecentlyCreatedAppointmentId(appointmentData?.id || "");
      setAppointmentSuccessFeedback(successSummary);
      resetAppointmentState();
      await loadData();
      if (typeof submitOptions.onSuccess === "function") {
        submitOptions.onSuccess({ ...appointmentData, successSummary });
      }
      return { ok: true, data: appointmentData };
    } catch (error) {
      const errorMessage = extractAppointmentSubmitError(error);
      const alternatives = extractAppointmentSubmitAlternatives(error);
      if (getBusinessHoursErrorMessage(error)) {
        showBlockingError(errorMessage);
      } else {
        toast.error(errorMessage);
      }
      if (typeof submitOptions.onError === "function") {
        submitOptions.onError({ errorMessage, alternatives, rawError: error });
      }
      return { ok: false, errorMessage, alternatives };
    }
  };

  const checkAvailability = async (event) => {
    event.preventDefault();
    if (!availabilityForm.service_id || !availabilityForm.date) {
      toast.error("Seleziona servizio e data.");
      return;
    }
    setAvailabilityLoading(true);
    try {
      const params = {
        service_id: availabilityForm.service_id,
        date: availabilityForm.date,
      };
      if (availabilityForm.staff_id && availabilityForm.staff_id !== "unassigned") {
        params.staff_id = availabilityForm.staff_id;
      }
      if (availabilityForm.requested_start_time) {
        params.requested_start_time = toIso(availabilityForm.requested_start_time);
      }
      const response = await api.get("/availability", { params });
      setAvailabilityResult(response.data);
      setSelectedSlot(null);
      toast.success("Disponibilità caricata.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Controllo disponibilità non riuscito.");
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const bookSelectedSlot = async () => {
    if (!selectedSlot) {
      toast.error("Seleziona uno slot disponibile.");
      return;
    }
    if (!availabilityForm.customer_id || !availabilityForm.service_id) {
      toast.error("Seleziona cliente e servizio.");
      return;
    }
    try {
      await api.post("/appointments", {
        customer_id: availabilityForm.customer_id,
        service_id: availabilityForm.service_id,
        staff_id: availabilityForm.staff_id === "unassigned" ? null : availabilityForm.staff_id,
        start_time: selectedSlot.start_time,
        status: "scheduled",
      });
      toast.success("Appuntamento confermato.");
      setSelectedSlot(null);
      loadData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail?.message) {
        toast.error(detail.message);
        if (detail.same_day_alternatives || detail.nearby_alternatives) {
          setAvailabilityResult((prev) => ({
            ...prev,
            alternatives: {
              same_day: detail.same_day_alternatives || [],
              nearby: detail.nearby_alternatives || [],
            },
          }));
        }
      } else {
        toast.error("Conferma slot non riuscita.");
      }
    }
  };

  const cancelAppointment = async (appointmentId) => {
    try {
      await api.post(`/appointments/${appointmentId}/cancel?reason=manual`);
      toast.success("Cancellazione registrata. Motore di recupero attivato.");
      await loadData();
      return true;
    } catch (error) {
      toast.error("Impossibile annullare l'appuntamento.");
      return false;
    }
  };

  const closeAppointmentLifecycle = async (appointmentId, action) => {
    const endpoint = action === "complete" ? "complete" : "noshow";
    const successMessage =
      action === "complete"
        ? "Appuntamento segnato come completato."
        : "Appuntamento segnato come no-show.";
    const fallbackError =
      action === "complete"
        ? "Impossibile completare l'appuntamento."
        : "Impossibile segnare il no-show.";

    setClosingAppointmentId(appointmentId);
    try {
      await api.post(`/appointments/${appointmentId}/${endpoint}`);
      toast.success(successMessage);
      await loadData();
      return true;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : fallbackError);
      return false;
    } finally {
      setClosingAppointmentId("");
    }
  };

  const closeRescheduleForm = () => {
    setReschedulingAppointmentId("");
    setRescheduleForm({ start_time: "" });
    setRescheduleSubmitting(false);
  };

  const openRescheduleForm = (appointment) => {
    setReschedulingAppointmentId(appointment.id);
    setRescheduleForm({
      start_time: toDateTimeLocalValue(appointment.start_time),
    });
  };

  const handleRescheduleStartChange = (value) => {
    setRescheduleForm({ start_time: value });
  };

  const submitReschedule = async (appointmentId) => {
    dismissBlockingError();
    if (!rescheduleForm.start_time) {
      toast.error("Seleziona il nuovo inizio.");
      return;
    }
    setRescheduleSubmitting(true);
    try {
      await api.post(`/appointments/${appointmentId}/reschedule`, {
        start_time: toIso(rescheduleForm.start_time),
      });
      toast.success("Appuntamento spostato con successo.");
      closeRescheduleForm();
      await loadData();
    } catch (error) {
      const errorMessage = getRescheduleErrorMessage(error);
      if (getBusinessHoursErrorMessage(error)) {
        showBlockingError(errorMessage);
      } else {
        toast.error(errorMessage);
      }
      setRescheduleSubmitting(false);
    }
  };

  const seedSampleData = async () => {
    setSeedLoading(true);
    try {
      await api.post("/testing/seed", {
        customers: 6,
        services: 4,
        staff: 3,
        appointments: 6,
        candidates: 4,
      });
      toast.success("Dati di esempio creati.");
      loadData();
    } catch (error) {
      toast.error("Impossibile creare i dati di esempio.");
    } finally {
      setSeedLoading(false);
    }
  };

  const createQuickScenario = async () => {
    setSeedLoading(true);
    try {
      const response = await api.post("/testing/scenario");
      toast.success("Scenario di test creato. Annulla l'appuntamento per avviare il recupero.");
      if (response.data?.appointment_id) {
        setLastScenarioId(response.data.appointment_id);
        toast.message(`ID appuntamento: ${response.data.appointment_id.slice(0, 6)}`);
      }
      loadData();
    } catch (error) {
      toast.error("Impossibile creare lo scenario di test.");
    } finally {
      setSeedLoading(false);
    }
  };

  const formatTime = (value) => formatDateTimeIT(value);

  const openAppointmentConfirmation = (action, appointment) => {
    successfulRiskyActionRef.current = null;
    setCompletedRiskyAction(null);
    setRiskyActionError("");
    setPendingRiskyAction({ action, appointment });
    setRiskyActionDialogOpen(true);
  };

  const openBlockedTimeConfirmation = (blockedTime) => {
    successfulRiskyActionRef.current = null;
    setCompletedRiskyAction(null);
    setRiskyActionError("");
    setPendingRiskyAction({ action: "deleteBlockedTime", blockedTime });
    setRiskyActionDialogOpen(true);
  };

  const closeRiskyActionConfirmation = () => {
    if (riskyActionSubmittingRef.current) {
      return;
    }
    setRiskyActionDialogOpen(false);
  };

  const confirmRiskyAction = async () => {
    if (!pendingRiskyAction || riskyActionSubmittingRef.current) {
      return;
    }

    riskyActionSubmittingRef.current = true;
    setRiskyActionSubmitting(true);
    setRiskyActionError("");
    const { action, appointment, blockedTime } = pendingRiskyAction;
    let succeeded = false;

    try {
      if (action === "cancel") {
        succeeded = await cancelAppointment(appointment.id);
      } else if (action === "complete" || action === "noshow") {
        succeeded = await closeAppointmentLifecycle(appointment.id, action);
      } else if (action === "deleteBlockedTime") {
        succeeded = await deleteBlockedTime(blockedTime.id);
      }

      if (!succeeded) {
        setRiskyActionError(RISKY_ACTION_COPY[action].errorMessage);
        return;
      }

      successfulRiskyActionRef.current = action;
      setCompletedRiskyAction(action);
      setRiskyActionDialogOpen(false);
    } finally {
      riskyActionSubmittingRef.current = false;
      setRiskyActionSubmitting(false);
    }
  };

  if (loading) {
    return <div data-testid="agenda-loading">Caricamento agenda...</div>;
  }

  if (agendaLoadState === "failure") {
    return (
      <section
        className="rounded-sm border border-red-300 bg-red-50 p-5 text-red-950"
        role="alert"
        data-testid="agenda-load-error"
      >
        <h1 className="text-lg font-semibold">Agenda non disponibile</h1>
        <p className="mt-2 text-sm">
          Non è stato possibile caricare gli appuntamenti. Lo stato vuoto non è attendibile.
        </p>
        <button
          type="button"
          onClick={loadData}
          className="mt-4 min-h-11 rounded-sm bg-red-800 px-4 text-sm font-semibold text-white"
          data-testid="agenda-load-retry"
        >
          Riprova
        </button>
      </section>
    );
  }

  if (mode === "booking") {
    return (
      <div className="mx-auto max-w-3xl space-y-4" data-testid="owner-booking-page">
        {appointmentSuccessFeedback ? (
          <OwnerAppointmentSuccess
            successSummary={appointmentSuccessFeedback}
            onBackToAgenda={handleBackToAgenda}
            onCreateAnotherAppointment={handleCreateAnotherAppointment}
            businessTimezone={businessTimezone}
          />
        ) : (
          <>
            <Link to="/" className="inline-flex min-h-11 items-center rounded-sm border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700">
              ← Torna all’Agenda
            </Link>
            <PageHeader
              title="Crea un nuovo appuntamento"
              subtitle="Inserisci una prenotazione nell’agenda."
            />
            <AppointmentWizardMobile
              standalone
              appointmentForm={appointmentForm}
              resetToken={appointmentWizardResetToken}
              customerMode={customerMode}
              customers={customers}
              services={activeServices}
              staff={activeStaff}
              quickCustomerForm={quickCustomerForm}
              onFieldChange={handleAppointmentFieldChange}
              onCustomerModeChange={handleCustomerModeChange}
              onQuickCustomerChange={handleQuickCustomerChange}
              onSubmit={submitAppointment}
              businessTimezone={businessTimezone}
              historicalOutcomeEnabled={isOwner}
            />
          </>
        )}
      </div>
    );
  }

  const statusLabels = {
    scheduled: "programmato",
    cancelled: "annullato",
    requested: "richiesto",
    recovered: "recuperato",
    completed: "completato",
    no_show: "no-show",
  };

  return (
    <div className="space-y-8" data-testid="agenda-page">
      {agendaRefreshError && (
        <div
          className="rounded-sm border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950"
          role="status"
          data-testid="agenda-refresh-error"
        >
          <p>{agendaRefreshError}</p>
          <button
            type="button"
            onClick={refreshAppointments}
            className="mt-3 min-h-11 rounded-sm border border-amber-400 bg-white px-4 font-semibold"
            data-testid="agenda-refresh-retry"
          >
            Riprova aggiornamento
          </button>
        </div>
      )}
      {mode === "tools" && (
        <div className="space-y-2">
          <Link to="/" className="inline-flex min-h-11 items-center rounded-sm border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700">
            ← Torna all’Agenda
          </Link>
          <PageHeader
            title="Gestisci orari staff"
            subtitle="Crea e gestisci i blocchi orari dello staff."
          />
        </div>
      )}
      {mode === "availability-lab" && (
        <div className="space-y-2">
          <Link to="/" className="inline-flex min-h-11 items-center rounded-sm border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700">
            ← Torna all’Agenda
          </Link>
          <PageHeader
            title="Verifica disponibilità base — Lab"
            subtitle="Strumento diagnostico owner-only in sola lettura, escluso dalla navigazione operativa."
          />
        </div>
      )}
      {mode === "agenda" && (
        <div className="space-y-4" data-testid="agenda-header-actions">
          <PageHeader
            title="Agenda"
            subtitle="Crea un nuovo appuntamento oppure consulta e gestisci quelli già presenti."
            titleTestId="agenda-title"
            subtitleTestId="agenda-subtitle"
          />
          <div
            className="flex min-w-0 flex-col items-start gap-3 sm:flex-row sm:items-center"
            data-testid="agenda-primary-actions"
          >
            <Link
              to="/appointments/new"
              className="inline-flex min-h-12 w-full min-w-0 items-center justify-center rounded-sm bg-slate-900 px-5 text-sm font-semibold text-white shadow-sm sm:w-auto"
              data-testid="agenda-new-appointment-link"
            >
              Crea un nuovo appuntamento
            </Link>
            <Link
              to="/agenda-tools"
              className="inline-flex min-h-11 max-w-full items-center justify-center rounded-sm border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700"
              data-testid="agenda-tools-link"
            >
              Gestisci orari staff
            </Link>
          </div>
        </div>
      )}
      {showTestingTools && (
        <section className="bg-white border border-slate-200 rounded-sm p-6" data-testid="sample-data-card">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold section-title" data-testid="sample-data-title">
                Dati di test
              </h2>
              <p className="text-sm text-slate-500" data-testid="sample-data-subtitle">
                Crea rapidamente clienti, servizi, staff e scenari per testare il recupero.
              </p>
              {lastScenarioId && (
                <p className="text-xs text-slate-400 mt-2" data-testid="sample-data-last-scenario">
                  Ultimo scenario: {lastScenarioId.slice(0, 6)}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={seedSampleData}
                disabled={seedLoading}
                className="bg-slate-900 text-white rounded-sm px-4 py-2 text-sm font-medium disabled:opacity-70"
                data-testid="seed-sample-data-button"
              >
                {seedLoading ? "Generazione..." : "Crea dataset esempio"}
              </button>
              <button
                onClick={createQuickScenario}
                disabled={seedLoading}
                className="bg-white border border-slate-200 text-slate-900 rounded-sm px-4 py-2 text-sm font-medium disabled:opacity-70"
                data-testid="seed-quick-scenario-button"
              >
                {seedLoading ? "Generazione..." : "Crea scenario rapido"}
              </button>
              <button
                onClick={() => {
                  const appointment = appointments.find((item) => item.id === lastScenarioId);
                  if (appointment) openAppointmentConfirmation("cancel", appointment);
                }}
                disabled={!lastScenarioId}
                className="bg-white border border-slate-200 text-slate-900 rounded-sm px-4 py-2 text-sm font-medium disabled:opacity-60"
                data-testid="seed-cancel-scenario-button"
              >
                Annulla scenario creato
              </button>
            </div>
          </div>
        </section>
      )}
      {mode === "agenda" && <section
        className="rounded-sm border border-emerald-300 bg-emerald-50 p-5 shadow-sm sm:p-6"
        data-testid="next-client-widget"
      >
        {nextClientState.kind === "empty" ? (
          <div>
            <p
              className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-800"
              data-testid="next-client-label"
              data-visual-role="informational"
            >
              Prossimo appuntamento
            </p>
            <p className="mt-2 text-sm text-emerald-900">Nessun prossimo cliente programmato.</p>
          </div>
        ) : (() => {
          const appointment = nextClientState.appointment;
          const customer = customerMap[appointment.customer_id];
          const service = serviceMap[appointment.service_id];
          const customerName = appointment.customer_name || customer?.name || "Cliente";
          const serviceName = appointment.service_name || service?.name || "Servizio non disponibile";
          const staffName = appointment.staff_name || staffMap[appointment.staff_id]?.name || "Non assegnato";
          const canCloseFromCard =
            isOwner &&
            appointment.status === "scheduled" &&
            Number.isFinite(Date.parse(appointment.start_time)) &&
            Date.parse(appointment.start_time) <= agendaNow.getTime();
          const isClosing = closingAppointmentId === appointment.id;
          const operationalLabel =
            nextClientState.kind === "current"
              ? "In corso"
              : "Programmato";
          const focusCardAppointment = () => {
            setSelectedDayKey(toBusinessDateKey(appointment.start_time, businessTimezone));
            setJumpDate(toBusinessDateKey(appointment.start_time, businessTimezone));
            setSelectedAgendaSection("day");
            setHighlightedAppointmentId(appointment.id);
          };
          return (
            <div className="space-y-5" data-testid="next-appointment-card">
              <div className="min-w-0">
                <p
                  className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-800"
                  data-testid="next-client-label"
                  data-visual-role="informational"
                >
                  Prossimo appuntamento
                </p>
                <p className="mt-2 text-sm text-emerald-900" data-testid="next-client-description">
                  Ecco il prossimo appuntamento in agenda.
                </p>
                <dl className="mt-3 grid gap-2 text-sm text-emerald-900 sm:grid-cols-2">
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-emerald-700">Cliente</dt>
                    <dd className="break-words font-semibold text-emerald-950" data-testid="next-client-name">
                      {customerName}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-emerald-700">Servizio</dt>
                    <dd data-testid="next-client-service">{serviceName}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-emerald-700">Staff</dt>
                    <dd data-testid="next-client-staff">{staffName}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-emerald-700">Orario</dt>
                    <dd data-testid="next-client-time">
                      {formatAgendaAppointmentDateTime(appointment.start_time, businessTimezone)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-emerald-700">Stato</dt>
                    <dd data-testid="next-client-status">{operationalLabel}</dd>
                  </div>
                </dl>
              </div>
              <div className="flex flex-col gap-2 border-t border-emerald-200 pt-4 sm:flex-row sm:flex-wrap" data-testid="next-client-actions">
                <button
                  type="button"
                  onClick={focusCardAppointment}
                  className="min-h-11 rounded-sm border border-emerald-300 bg-white px-4 text-sm font-semibold text-emerald-900"
                  data-testid="next-client-open-day"
                >
                  Vai all’appuntamento
                </button>
                {canCloseFromCard && (
                  <>
                    <button
                      type="button"
                      onClick={() => openAppointmentConfirmation("complete", appointment)}
                      disabled={isClosing}
                      className="min-h-11 rounded-sm bg-emerald-700 px-4 text-sm font-semibold text-white disabled:opacity-70"
                      data-testid={`next-client-complete-${appointment.id}`}
                    >
                      {isClosing ? "Aggiornamento..." : "Svolto"}
                    </button>
                    <button
                      type="button"
                      onClick={() => openAppointmentConfirmation("noshow", appointment)}
                      disabled={isClosing}
                      className="min-h-11 rounded-sm border border-amber-300 bg-white px-4 text-sm font-semibold text-amber-800 disabled:opacity-70"
                      data-testid={`next-client-noshow-${appointment.id}`}
                    >
                      No-show
                    </button>
                  </>
                )}
                {agendaSections.closing.length > 0 && nextClientState.kind !== "closing" && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedAgendaSection("closing");
                      setHighlightedAppointmentId("");
                    }}
                    className="min-h-11 rounded-sm border border-amber-300 bg-amber-50 px-4 text-sm font-semibold text-amber-900"
                    data-testid="next-client-open-closing"
                  >
                    Da chiudere · {agendaSections.closing.length}
                  </button>
                )}
              </div>
            </div>
          );
        })()}
      </section>}
      {mode === "tools" && <section className="grid gap-6" data-testid="agenda-forms">
        <div
          ref={blockedTimesSectionRef}
          tabIndex={-1}
          className="bg-white border border-slate-200 rounded-sm p-6 space-y-4 outline-none"
          data-testid="blocked-time-card"
        >
          <div>
            <h2 className="text-lg font-semibold section-title" data-testid="blocked-time-title">
              Blocca orario staff
            </h2>
            <p className="text-sm text-slate-500" data-testid="blocked-time-subtitle">
              Crea fasce non prenotabili distinte dagli appuntamenti.
            </p>
          </div>

          {isOwner ? (
            <form className="space-y-3" onSubmit={submitBlockedTime} data-testid="blocked-time-form">
              <Select
                value={blockedTimeForm.staff_id || ""}
                onValueChange={(value) => handleBlockedTimeFieldChange("staff_id", value)}
              >
                <SelectTrigger data-testid="blocked-time-staff-select">
                  <SelectValue placeholder="Seleziona staff" />
                </SelectTrigger>
                <SelectContent>
                  {activeStaff.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="grid gap-3 sm:grid-cols-2">
                <DateTimeField
                  label="Inizio blocco orario"
                  step="60"
                  value={blockedTimeForm.start_time}
                  onChange={(event) => handleBlockedTimeFieldChange("start_time", event.target.value)}
                  data-testid="blocked-time-start-input"
                />
                <DateTimeField
                  label="Fine blocco orario"
                  step="60"
                  value={blockedTimeForm.end_time}
                  onChange={(event) => handleBlockedTimeFieldChange("end_time", event.target.value)}
                  data-testid="blocked-time-end-input"
                />
              </div>
              <input
                type="text"
                aria-label="Motivo blocco orario"
                value={blockedTimeForm.reason}
                onChange={(event) => handleBlockedTimeFieldChange("reason", event.target.value)}
                placeholder="Motivo opzionale"
                className="border border-slate-200 rounded-sm h-10 px-3"
                data-testid="blocked-time-reason-input"
              />
              <button
                type="submit"
                disabled={blockedTimeSubmitting}
                className="w-full bg-slate-900 text-white rounded-sm h-11 font-medium disabled:opacity-70"
                data-testid="blocked-time-submit-button"
              >
                {blockedTimeSubmitting ? "Salvataggio..." : "Crea blocco orario"}
              </button>
            </form>
          ) : (
            <div
              className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600"
              data-testid="blocked-time-readonly-note"
            >
              Puoi vedere i blocchi orari esistenti, ma solo il proprietario può crearli o eliminarli.
            </div>
          )}

          <div className="space-y-3" data-testid="blocked-time-list">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-900">Blocchi orari attivi</p>
              <span className="text-xs uppercase tracking-[0.2em] text-slate-400">
                {blockedTimes.length}
              </span>
            </div>

            {blockedTimesLoading ? (
              <p className="text-sm text-slate-500" data-testid="blocked-time-loading">
                Caricamento blocchi orari...
              </p>
            ) : blockedTimes.length === 0 ? (
              <div
                className="rounded-sm border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500"
                data-testid="blocked-time-empty"
              >
                Nessun blocco orario presente.
              </div>
            ) : (
              <div className="space-y-3">
                {blockedTimes
                  .slice()
                  .sort((left, right) => Date.parse(left.start_time) - Date.parse(right.start_time))
                  .map((blockedTime) => (
                    <div
                      key={blockedTime.id}
                      className="rounded-sm border border-slate-200 px-4 py-3"
                      data-testid={`blocked-time-item-${blockedTime.id}`}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-slate-900">
                            {blockedTime.staff_name || staffMap[blockedTime.staff_id]?.name || "Staff sconosciuto"}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatTime(blockedTime.start_time)} — {formatTime(blockedTime.end_time)}
                          </p>
                          {blockedTime.reason ? (
                            <p className="text-xs text-slate-500">{blockedTime.reason}</p>
                          ) : (
                            <p className="text-xs text-slate-400">Nessun motivo specificato</p>
                          )}
                        </div>
                        {isOwner && (
                          <button
                            type="button"
                            onClick={() => openBlockedTimeConfirmation(blockedTime)}
                            disabled={blockedTimeDeletingId === blockedTime.id}
                            className="destructive-action rounded-sm px-3 py-2 text-xs font-medium disabled:opacity-60"
                            data-testid={`blocked-time-delete-${blockedTime.id}`}
                          >
                            {blockedTimeDeletingId === blockedTime.id ? "Eliminazione..." : "Elimina"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

      </section>}

      {mode === "availability-lab" && <section
        className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6"
        data-booking-helper-preserved={typeof bookSelectedSlot === "function" ? "true" : "false"}
        data-testid="availability-section"
      >
        <h2 className="text-lg font-semibold section-title" data-testid="availability-title">
          Verifica disponibilità base
        </h2>
        <p className="mt-1 text-sm text-slate-500">La diagnosi controlla gli slot senza creare o modificare appuntamenti.</p>
        <form className="mt-4 grid md:grid-cols-2 lg:grid-cols-5 gap-3" onSubmit={checkAvailability} data-testid="availability-form">
          <Select
            value={availabilityForm.service_id || ""}
            onValueChange={(value) => setAvailabilityForm((prev) => ({ ...prev, service_id: value }))}
          >
            <SelectTrigger data-testid="availability-service-select">
              <SelectValue placeholder="Servizio" />
            </SelectTrigger>
            <SelectContent>
              {activeServices.map((service) => (
                <SelectItem key={service.id} value={service.id}>
                  {service.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={availabilityForm.staff_id}
            onValueChange={(value) => setAvailabilityForm((prev) => ({ ...prev, staff_id: value }))}
          >
            <SelectTrigger data-testid="availability-staff-select">
              <SelectValue placeholder="Staff" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Qualsiasi staff</SelectItem>
              {activeStaff.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  {member.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DateField
            label="Data disponibilità"
            value={availabilityForm.date}
            onChange={(event) => setAvailabilityForm((prev) => ({ ...prev, date: event.target.value }))}
            data-testid="availability-date-input"
          />
          <DateTimeField
            label="Orario richiesto"
            value={availabilityForm.requested_start_time}
            onChange={(event) =>
              setAvailabilityForm((prev) => ({ ...prev, requested_start_time: event.target.value }))
            }
            data-testid="availability-requested-start-input"
          />
          <button
            type="submit"
            className="md:col-span-2 lg:col-span-5 bg-slate-900 text-white rounded-sm h-11 font-medium"
            data-testid="availability-submit-button"
          >
            {availabilityLoading ? "Controllo..." : "Controlla slot disponibili"}
          </button>
        </form>

        {availabilityResult && (
          <div className="mt-4 space-y-3" data-testid="availability-results">
            {(() => {
              const renderSlotButton = (slot, prefix = "availability-slot") => (
                <button
                  key={`${prefix}-${slot.start_time}`}
                  onClick={() => setSelectedSlot(slot)}
                  className={`text-left border rounded-sm p-2 text-xs ${
                    selectedSlot?.start_time === slot.start_time
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-200"
                  }`}
                  data-testid={`${prefix}-${slot.start_time}`}
                >
                  {formatTime(slot.start_time)} — {formatTime(slot.end_time)}
                </button>
              );

              return (
                <>
                  <p className="text-sm text-slate-500">
                    Slot disponibili: {availabilityResult.available_slots?.length || 0}
                  </p>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {(availabilityResult.available_slots || []).slice(0, 18).map((slot) => renderSlotButton(slot))}
                  </div>
                  {availabilityResult.alternatives && (
                    <div className="space-y-2" data-testid="availability-alternatives">
                      <p className="text-xs text-slate-500">
                        Alternative stesso giorno: {availabilityResult.alternatives.same_day?.length || 0}
                      </p>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {(availabilityResult.alternatives.same_day || [])
                          .slice(0, 12)
                          .map((slot) => renderSlotButton(slot, "availability-alt-same-day-slot"))}
                      </div>
                      <p className="text-xs text-slate-500">
                        Alternative vicine: {availabilityResult.alternatives.nearby?.length || 0}
                      </p>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {(availabilityResult.alternatives.nearby || [])
                          .slice(0, 12)
                          .map((slot) => renderSlotButton(slot, "availability-alt-nearby-slot"))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </section>}

      {mode === "agenda" && <section
        ref={agendaSectionRef}
        tabIndex={-1}
        className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6 outline-none"
        data-testid="agenda-list-section"
      >
        <div>
          <h2 className="text-lg font-semibold section-title" data-testid="agenda-list-title">
            {calendarView === "month" ? "Calendario mensile" : "Calendario settimanale"}
          </h2>
          <p className="mt-1 text-sm text-slate-500">Scegli una data per vedere gli appuntamenti della giornata.</p>
        </div>
        <div
          className="mt-3 grid w-full max-w-sm grid-cols-2 rounded-sm border border-slate-300 bg-slate-100 p-1"
          role="group"
          aria-label="Vista calendario"
          data-testid="agenda-view-selector"
        >
          <button
            type="button"
            aria-pressed={calendarView === "month"}
            onClick={() => setCalendarView("month")}
            className={`min-h-11 rounded-sm px-2 py-2 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 sm:text-sm ${
              calendarView === "month" ? "bg-slate-900 text-white" : "bg-transparent text-slate-700"
            }`}
            data-testid="agenda-view-month"
          >
            Vista mese
          </button>
          <button
            type="button"
            aria-pressed={calendarView === "week"}
            onClick={() => {
              setCalendarView("week");
              setSelectedAgendaSection("day");
              setHighlightedAppointmentId("");
            }}
            className={`min-h-11 rounded-sm px-2 py-2 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 sm:text-sm ${
              calendarView === "week" ? "bg-slate-900 text-white" : "bg-transparent text-slate-700"
            }`}
            data-testid="agenda-view-week"
          >
            Vista settimana
          </button>
        </div>
        <div className="mt-3 grid w-full min-w-0 max-w-full grid-cols-[minmax(0,1fr)] gap-3 overflow-hidden rounded-sm border border-slate-200 bg-slate-50 p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end" data-testid="agenda-go-to-date">
          <div className="min-w-0">
            <DateField
              label="Scegli il giorno da visualizzare"
              value={jumpDate}
              visibleValue={formatDateInputValueIT(jumpDate)}
              visibleValueTestId="agenda-go-to-date-readable"
              onChange={(event) => setJumpDate(event.target.value)}
              data-testid="agenda-go-to-date-input"
            />
          </div>
          <button type="button" onClick={() => { if (jumpDate) showSelectedDay(jumpDate); }} className="min-h-11 w-full whitespace-nowrap rounded-sm border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 sm:w-auto" data-testid="agenda-go-to-date-submit">Vai alla data</button>
        </div>
        {calendarView === "month" ? (
        <div
          className="mt-3 rounded-sm border border-slate-200 bg-slate-50 p-3"
          data-testid="agenda-month-view"
        >
            <p className="text-center text-base font-semibold text-slate-950" data-testid="agenda-month-label">
              {formatAgendaMonthLabel(selectedDayKey)}
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2" data-testid="agenda-month-navigation">
              <button
                type="button"
                onClick={() => showSelectedDay(shiftMonthKey(selectedDayKey, -1))}
                className="min-h-11 min-w-0 whitespace-nowrap rounded-sm border border-slate-200 bg-white px-2 py-2 text-xs font-medium text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
                aria-label="Mese precedente"
                data-testid="agenda-month-previous"
              >
                Prec.
              </button>
              <button
                type="button"
                onClick={() => showSelectedDay(todayKey)}
                className="min-h-11 min-w-0 whitespace-nowrap rounded-sm border border-slate-300 bg-white px-2 py-2 text-xs font-semibold text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
                aria-label="Torna a oggi"
                data-testid="agenda-month-today"
              >
                Oggi
              </button>
              <button
                type="button"
                onClick={() => showSelectedDay(shiftMonthKey(selectedDayKey, 1))}
                className="min-h-11 min-w-0 whitespace-nowrap rounded-sm border border-slate-200 bg-white px-2 py-2 text-xs font-medium text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
                aria-label="Mese successivo"
                data-testid="agenda-month-next"
              >
                Succ.
              </button>
            </div>
            <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[10px] font-medium uppercase text-slate-400">
              {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((day) => <span key={day}>{day}</span>)}
            </div>
            <div className="mt-1 grid grid-cols-7 gap-1" data-testid="agenda-month-days">
              {agendaMonthDays.map((day) => (
                <button
                  key={day.dateKey}
                  type="button"
                  onClick={() => showSelectedDay(day.dateKey)}
                  aria-label={`${formatAgendaDayLabel(day.dateKey)}, ${day.count} appuntamenti da gestire`}
                  aria-current={day.dateKey === todayKey ? "date" : undefined}
                  aria-pressed={day.selected}
                  className={`relative min-h-11 min-w-0 rounded-sm border p-1 text-xs font-medium focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 ${
                    day.selected
                      ? "border-slate-900 bg-slate-900 text-white"
                      : day.inCurrentMonth
                        ? "border-slate-200 bg-white text-slate-700"
                        : "border-slate-100 bg-slate-50 text-slate-300"
                  } ${day.dateKey === todayKey && !day.selected ? "ring-1 ring-emerald-500" : ""}`}
                  data-testid={`agenda-month-day-${day.dateKey}`}
                >
                  <span>{day.dayNumber}</span>
                  <AgendaDayCountBadge
                    count={day.count}
                    selected={day.selected}
                    testId={`agenda-month-count-${day.dateKey}`}
                  />
                </button>
              ))}
            </div>
        </div>
        ) : (
        <div
          className="mt-3 min-w-0 overflow-hidden rounded-sm border border-slate-200 bg-slate-50 p-2 sm:p-3"
          data-testid="agenda-week-view"
        >
          <p className="text-center text-base font-semibold text-slate-950" data-testid="agenda-week-context-label">
            {formatAgendaWeekContextLabel(selectedDayKey)}
          </p>
          <div
            className="mt-3 grid grid-cols-[2.75rem_minmax(0,1fr)_2.75rem] items-center gap-1"
            data-testid="agenda-week-navigation"
          >
            <button
              type="button"
              onClick={() => showSelectedDay(shiftWeekKey(selectedDayKey, -1))}
              className="min-h-11 min-w-0 rounded-sm border border-slate-200 bg-white text-xl font-medium text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
              aria-label="Settimana precedente"
              data-testid="agenda-week-previous"
            >
              ‹
            </button>
            <p className="min-w-0 text-center text-sm font-semibold text-slate-800" aria-live="polite" data-testid="agenda-week-label">
              {formatAgendaWeekLabel(selectedDayKey)}
            </p>
            <button
              type="button"
              onClick={() => showSelectedDay(shiftWeekKey(selectedDayKey, 1))}
              className="min-h-11 min-w-0 rounded-sm border border-slate-200 bg-white text-xl font-medium text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
              aria-label="Settimana successiva"
              data-testid="agenda-week-next"
            >
              ›
            </button>
          </div>
          <div className="mt-3 grid w-full min-w-0 grid-cols-7 gap-1" data-testid="agenda-week-days">
            {agendaWeekDays.map((day) => {
              const isToday = day.dateKey === todayKey;
              return (
                <button
                  key={day.dateKey}
                  type="button"
                  onClick={() => showSelectedDay(day.dateKey)}
                  aria-label={`${formatAgendaDayLabel(day.dateKey)}, ${day.count} ${day.count === 1 ? "appuntamento da gestire" : "appuntamenti da gestire"}`}
                  aria-current={isToday ? "date" : undefined}
                  aria-pressed={day.selected}
                  className={`min-h-16 min-w-0 rounded-sm border px-0.5 py-2 text-center font-medium focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 ${
                    day.selected
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-700"
                  }`}
                  data-date-key={day.dateKey}
                  data-testid={`agenda-week-day-${day.dateKey}`}
                >
                  <span className="block truncate text-[9px] uppercase tracking-wide sm:text-[10px]">{day.dayLabel}</span>
                  <span
                    className={`mx-auto mt-1 flex h-6 w-6 items-center justify-center rounded-full text-sm sm:h-7 sm:w-7 ${
                      isToday && !day.selected ? "border border-slate-900 text-slate-900" : "border border-transparent"
                    }`}
                    data-testid={`agenda-week-day-number-${day.dateKey}`}
                  >
                    {day.dateLabel}
                  </span>
                  <AgendaDayCountBadge
                    count={day.count}
                    selected={day.selected}
                    testId={`agenda-week-count-${day.dateKey}`}
                  />
                </button>
              );
            })}
          </div>
        </div>
        )}
        <div className="mt-4" data-testid="agenda-selected-day">
          {isGlobalClosingView ? (
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-4" data-testid="agenda-global-closing-summary">
              <h3 className="text-base font-semibold text-amber-950">Appuntamenti da chiudere</h3>
              {agendaSections.closing.length > 0 && (
                <p className="mt-1 text-sm text-amber-900">
                  Hai appuntamenti che richiedono una tua azione.
                </p>
              )}
              <button
                type="button"
                onClick={() => setSelectedAgendaSection("day")}
                className="mt-3 min-h-11 rounded-sm border border-amber-300 bg-white px-4 text-sm font-semibold text-amber-950"
                data-testid="agenda-return-to-day"
              >
                Torna a {formatAgendaDayLabel(selectedDayKey)}
              </button>
            </div>
          ) : (
            <div aria-live="polite" data-testid="agenda-day-summary">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Giornata selezionata</p>
              <h3 className="mt-1 text-base font-semibold text-slate-950" data-testid="agenda-selected-day-label">
                {formatAgendaDayLabel(selectedDayKey)}
              </h3>
              <p className="mt-1 text-sm text-slate-600" data-testid="agenda-day-count">
                {selectedDaySummary.totalLabel}
                {selectedDaySummary.statusLabel ? ` · ${selectedDaySummary.statusLabel}` : ""}
              </p>
            </div>
          )}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2" role="tablist" aria-label="Contesto agenda" data-testid="agenda-section-tabs">
          <button
            type="button"
            role="tab"
            aria-selected={!isGlobalClosingView}
            onClick={() => setSelectedAgendaSection("day")}
            className={`min-h-11 rounded-sm border px-3 py-2 text-sm font-semibold ${
              !isGlobalClosingView ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700"
            }`}
            data-testid="agenda-section-day"
          >
            Giornata · {selectedDaySummary.total}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isGlobalClosingView}
            onClick={() => { setSelectedAgendaSection("closing"); setHighlightedAppointmentId(""); }}
            className={`min-h-11 rounded-sm border px-3 py-2 text-sm font-semibold ${
              isGlobalClosingView ? "border-amber-700 bg-amber-700 text-white" : "border-amber-200 bg-amber-50 text-amber-900"
            }`}
            data-testid="agenda-section-closing"
          >
            Da chiudere · {agendaSections.closing.length}
            <span className="block text-[10px] font-medium">Tutti i giorni</span>
          </button>
        </div>
        {!isGlobalClosingView && (
          <div className="mt-3 flex flex-wrap gap-2" aria-label="Filtra la giornata per stato" data-testid="agenda-day-status-filters">
            {AGENDA_SECTION_DEFINITIONS.filter((section) => section.key !== "closing").map((section) => {
              const isSelected = selectedAgendaSection === section.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => setSelectedAgendaSection(isSelected ? "day" : section.key)}
                  className={`min-h-11 rounded-full border px-3 py-2 text-xs font-medium ${
                    isSelected ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600"
                  }`}
                  data-testid={`agenda-section-${section.key}`}
                >
                  {section.label} · {selectedDaySummary.sections[section.key].length}
                </button>
              );
            })}
          </div>
        )}
        <div className="mt-4 space-y-3" data-testid="agenda-list">
          {displayedAppointments.length === 0 && (
            <div
              className="rounded-sm border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-500"
              data-testid="agenda-section-empty"
            >
              {isGlobalClosingView
                ? "Non ci sono appuntamenti da chiudere."
                : selectedAgendaSection === "day"
                  ? `Nessun appuntamento per ${formatAgendaDayLabel(selectedDayKey)}.`
                  : `Nessun appuntamento ${AGENDA_SECTION_DEFINITIONS.find((section) => section.key === selectedAgendaSection)?.label.toLowerCase() || "con questo stato"} in questa giornata.`}
            </div>
          )}
          {agendaRows.map((row) => {
            if (row.type === "day") {
              if (!isGlobalClosingView) return null;
              return (
                <h3
                  key={`day-${row.dateKey}`}
                  className="pt-2 text-sm font-semibold text-slate-900"
                  data-testid={`agenda-day-heading-${row.dateKey}`}
                >
                  {row.label}
                </h3>
              );
            }
            const appointment = row.appointment;
            const service = serviceMap[appointment.service_id];
            const customer = customerMap[appointment.customer_id];
            const staffMember = staffMap[appointment.staff_id];
            const displayServiceName = appointment.service_name || service?.name || "Servizio";
            const displayCustomerName = appointment.customer_name || customer?.name || "Cliente";
            const displayCancelledCustomerName =
              appointment.customer_name || customer?.name || "Cliente sconosciuto";
            const displayStaffName = appointment.staff_name || staffMember?.name || "Non assegnato";
            const revenue = service ? `${formatCurrencyEUR(service.price)}` : "-";
            const isRecentlyCreated = appointment.id === recentlyCreatedAppointmentId;
            const isRescheduling = appointment.id === reschedulingAppointmentId;
            const appointmentDurationMinutes = getAppointmentDurationMinutes(appointment);
            const rescheduleExpectedEnd = isRescheduling
              ? getRescheduleExpectedEnd(rescheduleForm.start_time, appointmentDurationMinutes)
              : "";
            const appointmentStartMs = appointment.start_time ? Date.parse(appointment.start_time) : Number.NaN;
            const canCloseAppointment =
              appointment.status === "scheduled" &&
              Number.isFinite(appointmentStartMs) &&
              appointmentStartMs <= Date.now();
            const isClosing = closingAppointmentId === appointment.id;
            const isNoShow = appointment.status === "no_show";
            const isAppointmentHighlighted =
              appointment.id === linkedAppointmentId || appointment.id === highlightedAppointmentId;
            const appointmentCardTone = isNoShow
              ? `border-orange-600 bg-orange-200/90 ${isAppointmentHighlighted ? "ring-2 ring-orange-600 ring-offset-2" : ""}`
              : isAppointmentHighlighted
                ? "border-emerald-500 bg-emerald-50/40 ring-2 ring-emerald-500 ring-offset-2"
                : isRecentlyCreated
                  ? "border-emerald-400 bg-emerald-50/40"
                  : "border-slate-200";
            const appointmentStatusTone = isNoShow
              ? "font-bold text-orange-950"
              : appointment.status === "completed"
                ? "font-semibold text-emerald-700"
                : "text-slate-400";
            const isWeeklyDayDetail = calendarView === "week" && !isGlobalClosingView;

            if (appointment.status === "cancelled") {
              const cancelledCard = (
                <div
                  key={appointment.id}
                  id={`agenda-appointment-focus-${appointment.id}`}
                  tabIndex={-1}
                  className={`ghost-slot rounded-sm ${isWeeklyDayDetail ? "p-3 sm:p-4" : "p-4"} ${
                    appointment.id === linkedAppointmentId || appointment.id === highlightedAppointmentId ? "ring-2 ring-emerald-500 ring-offset-2" : ""
                  }`}
                  aria-current={appointment.id === linkedAppointmentId ? "true" : undefined}
                  data-highlighted={appointment.id === highlightedAppointmentId ? "true" : undefined}
                  data-testid={`agenda-appointment-${appointment.id}`}
                >
                  <div className={`flex gap-2 ${isWeeklyDayDetail ? "flex-col items-start sm:flex-row sm:items-center sm:justify-between" : "items-center justify-between"}`}>
                    <div className="min-w-0">
                      <p
                        className="text-sm font-medium"
                        data-testid={isWeeklyDayDetail ? `appointment-cancelled-label-${appointment.id}` : `appointment-time-${appointment.id}`}
                      >
                        {isWeeklyDayDetail ? "Annullato" : `${formatTime(appointment.start_time)} — Annullato`}
                      </p>
                      <p className="text-xs text-slate-500" data-testid={`appointment-customer-${appointment.id}`}>
                        {displayCancelledCustomerName}
                      </p>
                      {appointment.cancelled_by_role && (
                        <p className="text-xs text-slate-500" data-testid={`appointment-cancelled-by-${appointment.id}`}>
                          Annullato da: {CANCELLATION_ACTOR_LABELS[appointment.cancelled_by_role] || appointment.cancelled_by_role}
                        </p>
                      )}
                      {appointment.cancellation_reason && (
                        <p className="text-xs text-slate-400" data-testid={`appointment-cancellation-reason-${appointment.id}`}>
                          Motivo: {appointment.cancellation_reason}
                        </p>
                      )}
                    </div>
                    <div className={isWeeklyDayDetail ? "text-left sm:text-right" : "text-right"}>
                      <p className="text-xs uppercase tracking-[0.2em] text-orange-600" data-testid={`appointment-status-${appointment.id}`}>
                        {appointment.slot_status === "recovered" ? "Slot recuperato" : "Slot libero"}
                      </p>
                      <p className="mono-text text-sm text-orange-600" data-testid={`appointment-revenue-${appointment.id}`}>
                        Ricavo potenziale {revenue}
                      </p>
                    </div>
                  </div>
                </div>
              );

              if (!isWeeklyDayDetail) return cancelledCard;
              return (
                <div
                  key={`week-${appointment.id}`}
                  className="grid min-w-0 grid-cols-[2.5rem_minmax(0,1fr)] items-start gap-2 sm:grid-cols-[3.5rem_minmax(0,1fr)]"
                  data-testid={`agenda-week-appointment-row-${appointment.id}`}
                >
                  <time
                    dateTime={appointment.start_time}
                    className="pt-3 text-right text-[11px] font-semibold tabular-nums text-slate-700 sm:pt-4 sm:text-xs"
                    data-testid={`appointment-time-${appointment.id}`}
                  >
                    {formatAgendaAppointmentTime(appointment.start_time, businessTimezone)}
                  </time>
                  {cancelledCard}
                </div>
              );
            }

            const appointmentCard = (
              <div
                key={appointment.id}
                id={`agenda-appointment-focus-${appointment.id}`}
                tabIndex={-1}
                className={`border rounded-sm ${isWeeklyDayDetail ? "p-3 sm:p-4" : "p-4"} ${appointmentCardTone}`}
                data-highlighted={appointment.id === highlightedAppointmentId ? "true" : undefined}
                data-testid={`agenda-appointment-${appointment.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    {!isWeeklyDayDetail && (
                      <p className="text-sm font-medium" data-testid={`appointment-time-${appointment.id}`}>
                        {formatTime(appointment.start_time)}
                      </p>
                    )}
                    <p className="text-xs text-slate-500" data-testid={`appointment-service-${appointment.id}`}>
                      {displayServiceName} · {displayCustomerName}
                    </p>
                    <p className="text-xs text-slate-400" data-testid={`appointment-staff-${appointment.id}`}>
                      Staff: {displayStaffName}
                    </p>
                  </div>
                  <div className={`flex ${isWeeklyDayDetail ? "flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4" : "items-center gap-4"}`}>
                    <div>
                      <p className={`text-xs uppercase tracking-[0.2em] ${appointmentStatusTone}`} data-testid={`appointment-status-${appointment.id}`}>
                        {classifyAppointment(appointment, agendaNow) === "closing"
                          ? "da chiudere"
                          : statusLabels[appointment.status] || appointment.status}
                      </p>
                      <p className="mono-text text-sm text-emerald-600" data-testid={`appointment-revenue-${appointment.id}`}>
                        {revenue}
                      </p>
                    </div>
                    {appointment.status === "scheduled" && (
                      <div className={`flex flex-wrap items-center gap-2 ${isWeeklyDayDetail ? "justify-start sm:justify-end" : "justify-end"}`}>
                        <button
                          type="button"
                          onClick={() => openRescheduleForm(appointment)}
                          className="bg-slate-900 text-white rounded-sm px-3 py-2 text-xs font-medium"
                          data-testid={`appointment-reschedule-${appointment.id}`}
                        >
                          Sposta
                        </button>
                        <button
                          type="button"
                          onClick={() => openAppointmentConfirmation("cancel", appointment)}
                          className="destructive-action rounded-sm px-3 py-2 text-xs font-medium"
                          data-testid={`appointment-cancel-${appointment.id}`}
                        >
                          Annulla
                        </button>
                        {isOwner && canCloseAppointment && (
                          <>
                            <button
                              type="button"
                              onClick={() => openAppointmentConfirmation("complete", appointment)}
                              disabled={isClosing}
                              className="bg-emerald-600 text-white rounded-sm px-3 py-2 text-xs font-medium disabled:opacity-70"
                              data-testid={`appointment-complete-${appointment.id}`}
                            >
                              {isClosing ? "Aggiornamento..." : "Svolto"}
                            </button>
                            <button
                              type="button"
                              onClick={() => openAppointmentConfirmation("noshow", appointment)}
                              disabled={isClosing}
                              className="bg-white border border-amber-200 text-amber-700 rounded-sm px-3 py-2 text-xs font-medium disabled:opacity-70"
                              data-testid={`appointment-noshow-${appointment.id}`}
                            >
                              No-show
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {isRescheduling && (
                  <div
                    className="mt-4 border border-slate-200 rounded-sm bg-slate-50 p-4"
                    data-testid={`appointment-reschedule-form-${appointment.id}`}
                  >
                    <div className="space-y-1 text-sm text-slate-700">
                      <p data-testid={`appointment-reschedule-service-${appointment.id}`}>
                        <span className="font-medium text-slate-900">Servizio:</span> {displayServiceName}
                      </p>
                      <p data-testid={`appointment-reschedule-duration-${appointment.id}`}>
                        <span className="font-medium text-slate-900">Durata:</span>{" "}
                        {appointmentDurationMinutes ? `${appointmentDurationMinutes} min` : "Non disponibile"}
                      </p>
                      <p data-testid={`appointment-reschedule-staff-${appointment.id}`}>
                        <span className="font-medium text-slate-900">Operatore:</span> {displayStaffName}
                      </p>
                    </div>
                    <div className="mt-3 space-y-3">
                      <DateTimeField
                          label="Nuovo inizio"
                          value={rescheduleForm.start_time}
                          onChange={(event) => handleRescheduleStartChange(event.target.value)}
                          data-testid={`appointment-reschedule-start-${appointment.id}`}
                        />
                      <p className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm" data-testid={`appointment-reschedule-expected-end-${appointment.id}`}>
                        <span className="block text-xs font-medium uppercase tracking-wide text-slate-500">Fine prevista</span>
                        <span className="mt-1 block font-medium text-slate-900">
                          {rescheduleExpectedEnd ? formatTime(rescheduleExpectedEnd) : "Seleziona il nuovo inizio"}
                        </span>
                      </p>
                    </div>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                      <button
                        type="button"
                        onClick={() => submitReschedule(appointment.id)}
                        disabled={rescheduleSubmitting || !rescheduleForm.start_time}
                        className="min-h-11 w-full bg-emerald-600 text-white rounded-sm px-3 py-2 text-xs font-medium disabled:opacity-70 sm:w-auto"
                        data-testid={`appointment-reschedule-submit-${appointment.id}`}
                      >
                        {rescheduleSubmitting ? "Spostamento..." : "Conferma spostamento"}
                      </button>
                      <button
                        type="button"
                        onClick={closeRescheduleForm}
                        disabled={rescheduleSubmitting}
                        className="min-h-11 w-full bg-white border border-slate-200 text-slate-900 rounded-sm px-3 py-2 text-xs font-medium disabled:opacity-70 sm:w-auto"
                        data-testid={`appointment-reschedule-cancel-${appointment.id}`}
                      >
                        Annulla
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
            if (!isWeeklyDayDetail) return appointmentCard;
            return (
              <div
                key={`week-${appointment.id}`}
                className="grid min-w-0 grid-cols-[2.5rem_minmax(0,1fr)] items-start gap-2 sm:grid-cols-[3.5rem_minmax(0,1fr)]"
                data-testid={`agenda-week-appointment-row-${appointment.id}`}
              >
                <time
                  dateTime={appointment.start_time}
                  className="pt-3 text-right text-[11px] font-semibold tabular-nums text-slate-700 sm:pt-4 sm:text-xs"
                  data-testid={`appointment-time-${appointment.id}`}
                >
                  {formatAgendaAppointmentTime(appointment.start_time, businessTimezone)}
                </time>
                {appointmentCard}
              </div>
            );
          })}
        </div>
      </section>}

      <Dialog
        open={riskyActionDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeRiskyActionConfirmation();
        }}
      >
        {pendingRiskyAction && (() => {
          const { action, appointment, blockedTime } = pendingRiskyAction;
          const copy = RISKY_ACTION_COPY[action];
          const dialogCustomer = appointment
            ? appointment.customer_name || customerMap[appointment.customer_id]?.name || "Cliente sconosciuto"
            : "";
          const dialogService = appointment
            ? appointment.service_name || serviceMap[appointment.service_id]?.name || "Servizio non disponibile"
            : "";
          const dialogStaff = appointment
            ? appointment.staff_name || staffMap[appointment.staff_id]?.name || "Non assegnato"
            : blockedTime?.staff_name || staffMap[blockedTime?.staff_id]?.name || "Staff sconosciuto";

          return (
            <DialogContent
              className="w-[calc(100%-2rem)] max-w-md rounded-sm"
              data-testid="agenda-risky-action-dialog"
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                riskyActionBackRef.current?.focus();
              }}
              onCloseAutoFocus={(event) => {
                if (successfulRiskyActionRef.current) event.preventDefault();
                setRiskyActionError("");
              }}
              onEscapeKeyDown={(event) => {
                if (riskyActionSubmitting) event.preventDefault();
              }}
              onPointerDownOutside={(event) => event.preventDefault()}
            >
              <DialogHeader>
                <DialogTitle data-testid="agenda-risky-action-title">{copy.title}</DialogTitle>
                <DialogDescription>
                  Controlla i dettagli prima di confermare questa azione.
                </DialogDescription>
              </DialogHeader>

              <dl className="space-y-3 rounded-sm border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="agenda-risky-action-context">
                {appointment ? (
                  <>
                    <div><dt className="text-slate-500">Cliente</dt><dd className="font-medium text-slate-900">{dialogCustomer}</dd></div>
                    <div><dt className="text-slate-500">Servizio</dt><dd className="font-medium text-slate-900">{dialogService}</dd></div>
                    <div><dt className="text-slate-500">Data e ora</dt><dd className="font-medium text-slate-900">{formatTime(appointment.start_time)}</dd></div>
                    <div><dt className="text-slate-500">Operatrice</dt><dd className="font-medium text-slate-900">{dialogStaff}</dd></div>
                  </>
                ) : (
                  <>
                    <div><dt className="text-slate-500">Operatrice</dt><dd className="font-medium text-slate-900">{dialogStaff}</dd></div>
                    <div><dt className="text-slate-500">Data e intervallo</dt><dd className="font-medium text-slate-900">{formatTime(blockedTime.start_time)} — {formatTime(blockedTime.end_time)}</dd></div>
                    <div><dt className="text-slate-500">Motivo</dt><dd className="font-medium text-slate-900">{blockedTime.reason || "Nessun motivo specificato"}</dd></div>
                  </>
                )}
              </dl>

              <p className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="agenda-risky-action-consequence">
                {copy.consequence}
              </p>

              {riskyActionError && (
                <p className="rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert" data-testid="agenda-risky-action-error">
                  {riskyActionError}
                </p>
              )}

              <DialogFooter className="gap-2 sm:space-x-0">
                <button
                  ref={riskyActionBackRef}
                  type="button"
                  onClick={closeRiskyActionConfirmation}
                  disabled={riskyActionSubmitting}
                  className="min-h-11 rounded-sm border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 disabled:opacity-60"
                  data-testid="agenda-risky-action-back"
                >
                  Indietro
                </button>
                <button
                  type="button"
                  onClick={confirmRiskyAction}
                  disabled={riskyActionSubmitting}
                  className="min-h-11 rounded-sm bg-slate-900 px-4 text-sm font-semibold text-white disabled:opacity-60"
                  data-testid="agenda-risky-action-confirm"
                >
                  {riskyActionSubmitting ? "Aggiornamento..." : copy.confirmLabel}
                </button>
              </DialogFooter>
            </DialogContent>
          );
        })()}
      </Dialog>
    </div>
  );
}



